import type { BootstrapShop } from '@/shared/api/bootstrap';
import type { SubscriptionPlan } from '@/shared/types/domain/subscription.types';
import { SubscriptionTier } from '@/shared/types/enums';

export type ShopExpansionBlockReason =
  | 'subscription_required'
  | 'plan_not_eligible'
  | 'limit_reached';

type PlanLike = Pick<SubscriptionPlan, 'tier' | 'limits' | 'name'>;

const DEFAULT_MAX_SHOPS_BY_TIER: Record<SubscriptionTier, number> = {
  [SubscriptionTier.SOLO]: 0,
  [SubscriptionTier.PRO]: 1,
  [SubscriptionTier.PRO_PLUS]: -1,
};

const getNumericLimit = (
  limits: Record<string, unknown> | null | undefined,
  key: 'maxShops' | 'maxLocations',
): number | null => {
  const value = limits?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const resolveMaxShops = (plan?: PlanLike | null): number => {
  const maxShops = getNumericLimit(plan?.limits, 'maxShops');
  if (maxShops !== null) return maxShops;

  const legacyMaxLocations = getNumericLimit(plan?.limits, 'maxLocations');
  if (legacyMaxLocations !== null) return legacyMaxLocations;

  if (plan?.tier && plan.tier in DEFAULT_MAX_SHOPS_BY_TIER) {
    return DEFAULT_MAX_SHOPS_BY_TIER[plan.tier as SubscriptionTier];
  }

  return 1;
};

export const countOwnedBootstrapShops = (
  shops: Pick<BootstrapShop, 'role'>[] = [],
): number => shops.filter(shop => shop.role === 'OWNER').length;

export const formatShopSlotLabel = (
  currentShopCount: number,
  maxShops: number,
): string => {
  const locationLabel = currentShopCount === 1 ? 'location' : 'locations';

  if (maxShops === -1) {
    return `${currentShopCount} ${locationLabel} used, unlimited`;
  }

  return `${currentShopCount} of ${maxShops} locations used`;
};

export const getShopExpansionBlockReason = ({
  currentShopCount,
  maxShops,
  hasActiveSubscription,
}: {
  currentShopCount: number;
  maxShops: number;
  hasActiveSubscription: boolean;
}): ShopExpansionBlockReason | null => {
  if (currentShopCount > 0 && !hasActiveSubscription) {
    return 'subscription_required';
  }

  if (maxShops === -1) {
    return null;
  }

  if (maxShops <= 0) {
    return 'plan_not_eligible';
  }

  if (maxShops !== -1 && currentShopCount >= maxShops) {
    return 'limit_reached';
  }

  return null;
};

export const getShopExpansionBlockedMessage = (
  reason: ShopExpansionBlockReason | null,
  planName?: string | null,
): string => {
  switch (reason) {
    case 'subscription_required':
      return 'An active subscription is required to add another location.';
    case 'plan_not_eligible':
      return `${planName || 'Your current plan'} does not include additional locations.`;
    case 'limit_reached':
      return `${planName || 'Your current plan'} has reached its location limit. Upgrade to Pro Plus to add more locations.`;
    default:
      return '';
  }
};
